/**
 * The coffee machine, for a machine with a Manetta board: its pill in the top bar (only once one is connected or
 * connecting), its line on setup and Home, and the card on the Scale page that connects it, shows its state and turns
 * it on and off.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { isBluetoothSupported } from '@/scale/bluetooth';
import { autoOffLabel, connectionBadge, machineBadge, machineLine, onForLabel } from '@/machine/labels';
import { getVirtualMachine, useMachine } from '@/machine/store';
import { Icon } from './icons';
import { Badge, Banner, Button, Card, Spinner, cx } from './ui';

/** Re-renders every `ms` while mounted, for a clock-driven label ("On for 4 min"). */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => performance.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(performance.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

export function MachineStatusPill() {
  const status = useMachine((s) => s.status);
  const state = useMachine((s) => s.state);
  const name = useMachine((s) => s.deviceName);
  if (status === 'disconnected') return null;
  const badge = connectionBadge(status, machineBadge(state));
  return (
    // A Link, not a NavLink like the scale's: both lead to the Scale page, and only one should mark it current.
    <Link to="/scale#machine" className="scale-pill machine-pill" title={name ? `${name}: ${badge.text}` : badge.text} aria-label={`Coffee machine: ${badge.text}`}>
      <Icon name="machine" size={16} />
      <span className={cx('dot', badge.dot)} />
      <span className="machine-pill-text">{badge.text}</span>
    </Link>
  );
}

/**
 * The guided brew's header: the machine next to the scale, only while one is connected (or coming back). A machine
 * that was lost is a Reconnect button, so a shot can still be started from Taretto without leaving the brew.
 */
export function GuidedMachineStatus() {
  const status = useMachine((s) => s.status);
  const state = useMachine((s) => s.state);
  const reconnect = useMachine((s) => s.reconnect);
  if (status === 'disconnected') return null;
  if (status === 'error') {
    return (
      <button type="button" className="bw-gscale bw-gmachine lost" onClick={() => void reconnect()} aria-label="Coffee machine lost. Reconnect" title="Machine lost. Tap to reconnect.">
        <Icon name="refresh" size={16} />
        <span className="dot err" />
        <span className="bw-gscale-text bw-gmachine-text">Reconnect</span>
      </button>
    );
  }
  const badge = connectionBadge(status, machineBadge(state));
  return (
    <span className="bw-gscale bw-gmachine" role="img" aria-label={`Coffee machine: ${badge.text}`} title={badge.text}>
      <Icon name="machine" size={16} className="faint" />
      <span className={cx('dot', badge.dot)} />
      <span className="bw-gscale-text bw-gmachine-text">{badge.text}</span>
    </span>
  );
}

/**
 * The machine where a shot starts from (setup's Start, Home): early enough that one that heats for a minute or more
 * does it while the beans are weighed and ground, with Turn on or Reconnect right there. Nothing without a machine.
 */
export function MachineStateLine({ where, className }: { where: 'setup' | 'home'; className?: string }) {
  const status = useMachine((s) => s.status);
  const state = useMachine((s) => s.state);
  const info = useMachine((s) => s.info);
  const pending = useMachine((s) => s.pending);
  const notice = useMachine((s) => s.notice);
  const setPower = useMachine((s) => s.setPower);
  const reconnect = useMachine((s) => s.reconnect);
  const line = machineLine(status, state, info);
  if (!line) return null;
  // A Turn on that didn't take says so in the note's place.
  const note = line.action === 'turnOn' && notice ? notice : line.note;
  return (
    <div className={cx('machine-line', className)}>
      <span className="machine-line-state" aria-live="polite">
        <Icon name="machine" size={16} className="faint" />
        <span className={cx('dot', line.dot)} />
        <span className="machine-line-text">{line.text}</span>
        {note && <span className="muted">{note}</span>}
      </span>
      {line.action === 'turnOn' && (
        <Button size="sm" loading={pending === 'turnOn'} disabled={pending != null} onClick={() => void setPower(true, where)}>
          Turn on
        </Button>
      )}
      {line.action === 'reconnect' && (
        <Button size="sm" icon="refresh" onClick={() => void reconnect()}>
          Reconnect
        </Button>
      )}
    </div>
  );
}

export function MachineCard() {
  const status = useMachine((s) => s.status);
  return (
    <Card id="machine" title="Coffee machine" className="machine-card">
      {status === 'connected' ? <ConnectedMachine /> : <MachineConnectControls />}
    </Card>
  );
}

function ConnectedMachine() {
  const name = useMachine((s) => s.deviceName);
  const info = useMachine((s) => s.info);
  const state = useMachine((s) => s.state);
  // A power command's, else a steam command's (switching back after a milk drink may have been left to the person).
  const notice = useMachine((s) => s.notice ?? s.steamNotice);
  const pending = useMachine((s) => s.pending);
  const pairing = useMachine((s) => s.pairing);
  const virtual = useMachine((s) => s.virtual);
  const disconnect = useMachine((s) => s.disconnect);
  const setPower = useMachine((s) => s.setPower);
  const now = useNow(30_000);
  const badge = machineBadge(state);
  const power = info?.capabilities.power ?? null;
  const onFor = onForLabel(state?.onSince ?? null, now);
  const autoOff = autoOffLabel(info);
  // Unknown power: offer both, and let the device answer.
  const off = state?.power === 'off';
  const known = state?.power != null;
  const offerOn = power?.turnOn && (off || !known);
  const offerOff = power?.turnOff && (!off || !known);
  const busy = pending != null;

  return (
    <div className="stack">
      <div className="row-wrap machine-head">
        <span className="row text-sm">
          <span className="dot ok" /> {name}
        </span>
        {import.meta.env.DEV && virtual && <Badge>Virtual</Badge>}
        <Button size="sm" variant="ghost" onClick={() => void disconnect()} disabled={busy}>
          Disconnect
        </Button>
      </div>
      <div className="machine-state" aria-live="polite">
        <span className={cx('dot', badge.dot)} />
        <span className="machine-state-text">{badge.text}</span>
        {onFor && state?.power !== 'off' && <span className="muted text-sm">{onFor}</span>}
      </div>
      {badge.hint && <p className="text-sm muted">{badge.hint}</p>}
      {(offerOn || offerOff) && (
        <div className="row-wrap">
          {offerOn && (
            <Button variant="primary" loading={pending === 'turnOn'} disabled={busy} onClick={() => void setPower(true)}>
              Turn on
            </Button>
          )}
          {offerOff && (
            <Button loading={pending === 'turnOff'} disabled={busy} onClick={() => void setPower(false)}>
              Turn off
            </Button>
          )}
        </div>
      )}
      {pairing && (
        <p className="row text-sm muted">
          <Spinner size={16} /> Pairing with the machine. Accept the request if this device asks.
        </p>
      )}
      {notice && <Banner tone="warning">{notice}</Banner>}
      {!power && <p className="text-sm muted">This machine’s board doesn’t report or switch its power.</p>}
      {autoOff && <p className="text-xs faint">{autoOff}</p>}
      {info && (
        <p className="text-xs faint">
          {[info.model && info.model !== name ? info.model : null, info.firmware, `Manetta ${info.protocolVersion}`].filter(Boolean).join(' · ')}
        </p>
      )}
      {import.meta.env.DEV && virtual && <VirtualMachineButtons />}
    </div>
  );
}

function MachineConnectControls() {
  const status = useMachine((s) => s.status);
  const name = useMachine((s) => s.deviceName);
  const error = useMachine((s) => s.error);
  const known = useMachine((s) => s.knownMachine);
  const connect = useMachine((s) => s.connect);
  const reconnect = useMachine((s) => s.reconnect);
  const disconnect = useMachine((s) => s.disconnect);
  const bt = isBluetoothSupported();
  const busy = status === 'connecting' || status === 'reconnecting';
  const offerReconnect = known != null && !busy && bt;

  return (
    <div className="stack-sm">
      <p className="text-sm muted">For a coffee machine with a Manetta board: see whether it’s on and ready, turn it on and off, and start and stop the shot and switch to steam from the guided brew.</p>
      {busy ? (
        <div className="row-wrap">
          <span className="row text-sm muted">
            <Spinner size={16} /> {status === 'connecting' ? `Connecting${name ? ` to ${name}` : ''}…` : 'Reconnecting to the machine…'}
          </span>
          <Button size="sm" variant="ghost" icon="x" onClick={() => void disconnect()}>
            Cancel
          </Button>
        </div>
      ) : (
        <div className="row-wrap">
          {offerReconnect && (
            <Button variant="primary" icon="refresh" title={`Reconnect ${known}`} onClick={() => void reconnect()}>
              <span className="truncate">Reconnect {known}</span>
            </Button>
          )}
          {/* connect() opens the picker synchronously inside the click, as it needs the user activation. */}
          <Button variant={offerReconnect ? 'ghost' : 'default'} icon="bluetooth" disabled={!bt} onClick={() => void connect()}>
            {offerReconnect ? 'Choose machine…' : 'Connect machine'}
          </Button>
          {import.meta.env.DEV && (
            <Button variant="ghost" icon="sparkles" onClick={() => void connect({ virtual: true })}>
              Virtual machine
            </Button>
          )}
        </div>
      )}
      {!bt && <span className="text-xs faint">No Web Bluetooth in this browser. Use Bluefy on iPad or iPhone, Chrome or Edge elsewhere.</span>}
      {error && <Banner tone="danger">{error}</Banner>}
    </div>
  );
}

/** Dev builds: the virtual machine's own buttons, as someone at the machine would press them. */
function VirtualMachineButtons() {
  const [, setTick] = useState(0);
  const press = (fn: () => void) => () => {
    fn();
    setTick((n) => n + 1);
  };
  const m = getVirtualMachine();
  if (!m) return null;
  return (
    <div className="stack-sm">
      <span className="text-xs faint">On the virtual machine</span>
      <div className="row-wrap">
        <Button size="sm" variant="ghost" onClick={press(() => m.machine.pressPowerButton())}>
          Power
        </Button>
        <Button size="sm" variant="ghost" onClick={press(() => m.machine.pressSteamButton())}>
          Steam
        </Button>
        <Button size="sm" variant="ghost" onClick={press(() => m.machine.pressMachineButton())}>
          Brew
        </Button>
        <Button size="sm" variant="ghost" onClick={press(() => m.gatt.drop())}>
          Drop the link
        </Button>
      </div>
    </div>
  );
}
